import { AfterViewInit, Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  BROADCAST_CHANNEL_LOGOUT,
  BROADCAST_CHANNEL_LOGOUT_MESSAGE,
} from 'src/app/core/component/sidenav/side-nav.component';
import { AvatarIconComponent } from '../avatar-icon/avatar-icon.component';
import { Router, RouterLink } from "@angular/router";
import { HELP_CENTER_URL, ORCA_HELP_MAIL } from 'src/app/util/common/links';
import { DEFAULT_DROP_DOWN_DELAY_TIME, DelayService } from "../../../../../core/service/delay.service";
import { AuthBackendService } from "../../../../auth/service/auth-backend.service";
import { fadeInOutAnimation } from "../../../../../util/animations/fade-in-out.animation";
import { AllIconsComponent } from "../../../../shared/component/all-icons/all-icons.component";
import {
  ModalExpiredSessionComponent,
} from "../../../../../core/component/modal-expired-session/modal-expired-session.component";
import { OrganizationsService } from "../../../../organization/service/organizations.service";
import { filter, takeUntil } from "rxjs/operators";
import { Subject, tap } from "rxjs";
import { EmployeeRole } from "../../../employee/dto/role-type";
import { FRONT_URLS } from "../../../../../util/common/url-parts";
import { UnsavedChangesModalService } from "../../../../shared/component/unsaved-changes-modal-wrapper/service/unsaved-changes-modal.service";


@Component({
  selector: 'app-nav-avatar',
  templateUrl: './nav-avatar.component.html',
  styleUrl: './nav-avatar.component.scss',
  standalone: true,
  imports: [
    AvatarIconComponent,
    ModalExpiredSessionComponent,
    AllIconsComponent,
    ModalExpiredSessionComponent,
  ],
  animations: [fadeInOutAnimation(200, 0)],
  providers: [DelayService],
})
/**
 * NavAvatarComponent is designed for use in {@link HorizontalNavBarComponent} and {@link EntranceComponent} where a user’s avatar
 * is accompanied by extra dropdown functionality. It provides a drop-down menu with actions such as:
 *
 * - Account settings
 * - Organization settings
 * - User management
 * - Support and log out options
 *
 * **Usage Guidelines:**
 * - **Dropdown interaction:** Use this component when you need the full set of
 *   interactive options (e.g. in hornavbar or entrance).
 * - **Simple Icon Display:** For cases where only the avatar image or initials are needed
 *   (without the extra functionality), please use the {@link AvatarIconComponent} instead.
 */
export class NavAvatarComponent implements OnInit, OnDestroy, AfterViewInit {
  private _destroyStream: Subject<void> = new Subject<void>();
  private orgService = inject(OrganizationsService);
  private authService = inject(AuthBackendService);
  private delayService = inject(DelayService);
  private router = inject(Router);
  private unsavedChangesService = inject(UnsavedChangesModalService);

  private readonly helpDeskUrl = HELP_CENTER_URL;
  protected readonly FRONT_URLS = FRONT_URLS;

  showUserManagementOptions = false; // is needed to show user management tabs
  isOrgAdmin = false; // is needed to show user management tabs text, according to user accesses

  name = '';
  showAvatarMenu = false;
  // when user makes log out it sends signal to another tabs to synchronize log out for all tabs where user was working
  broadcasterLogOut = new BroadcastChannel(BROADCAST_CHANNEL_LOGOUT);
  showExpiredSessionModal = false;

  ngOnInit(): void {
    this.initSubscriptions();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  ngAfterViewInit(): void {
    this.delayService.initSubscribes(DEFAULT_DROP_DOWN_DELAY_TIME,
      () => {
        this.showAvatarMenu = true;
      },
      () => {
        this.showAvatarMenu = false;
      });
  }

  activateAvatarMenu(): void {
    this.delayService.activate();
  }

  deactivateAvatarMenu(): void {
    this.delayService.deactivate();
  }

  /** Method used for triggering the functionality of the `broadcasterLogOut`'s subscribers */
  async logout(): Promise<void> {
    await this.authService.logout();
    this.broadcasterLogOut.postMessage(BROADCAST_CHANNEL_LOGOUT_MESSAGE);
  }

  mailToSupport(): void {
    window.location.href = `mailto:${ORCA_HELP_MAIL}?subject=Request Demo`;
  }

  async goToLogin(): Promise<void> {
    await this.router.navigate([FRONT_URLS.LOGIN]);
    this.showExpiredSessionModal = false;
  }

  async navigateWithCheck(url: string): Promise<void> {
    const canProceed = await this.unsavedChangesService.checkCanProceed();
    if (!canProceed) {
      return;
    }
    await this.router.navigate([url], { queryParamsHandling: 'preserve' });
  }

  private initSubscriptions(): void {
    this.orgService.selectedWithAccesses$
      .pipe(
        filter((org) => Boolean(org)),
        tap((org) => {
          this.isOrgAdmin = org.userAccesses.isOrgAdmin;
          this.showUserManagementOptions = org.userAccesses.fmsAccess === EmployeeRole.ADMIN || org.userAccesses.isOrgAdmin;
        }),
        takeUntil(this._destroyStream))
      .subscribe();
  }
}


